import React, { useEffect, useState } from 'react';
import { Loader2, Rocket, Pause, Play, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from './IntelligenceLayout';
import CRMCompanyDetail from './CRMCompanyDetail';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CRM_CONFIG_DEFAULTS = { pipeline_stages: [], account_statuses: [] };

const STATUS_STYLE = {
  active: 'text-emerald-600 bg-emerald-50', paused: 'text-amber-600 bg-amber-50',
  completed: 'text-blue-600 bg-blue-50', cancelled: 'text-gray-500 bg-gray-100',
};

const CRMSequences = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [config, setConfig] = useState(CRM_CONFIG_DEFAULTS);

  const load = () => {
    setLoading(true);
    Promise.all([
      fetchWithAuth(`${API_URL}/api/crm/sequences`),
      fetchWithAuth(`${API_URL}/api/crm/sequences/stats`),
    ]).then(([list, s]) => { setItems(list.items || []); setStats(s); })
      .catch(() => toast.error('Could not load sequences'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    fetchWithAuth(`${API_URL}/api/crm/config`).then(setConfig).catch(() => {});
  }, []);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/sequences/${id}/${action}`, { method: 'POST' });
      toast.success(`Sequence ${action}d`);
      load();
    } catch (e) {
      toast.error(e?.message || `Could not ${action} sequence`);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div data-testid="crm-sequences-page">
      {stats && (
        <div className="grid grid-cols-4 gap-3 mb-4" data-testid="sequence-stats">
          <StatCard label="Active" value={stats.active} testid="stat-active" />
          <StatCard label="Paused" value={stats.paused} testid="stat-paused" />
          <StatCard label="Completed" value={stats.completed} testid="stat-completed" />
          <StatCard label="Touches Sent" value={stats.touches_sent} testid="stat-touches-sent" />
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center" data-testid="no-sequences">No sequences yet. Start one from a company's detail view.</p>
      ) : items.map(seq => (
        <div key={seq.id} className="border border-gray-100 rounded-xl p-3.5 mb-2 flex items-center justify-between" data-testid={`sequence-row-${seq.id}`}>
          <div className="cursor-pointer" onClick={() => setSelectedCompanyId(seq.company_id)}>
            <div className="flex items-center gap-2">
              <span className="font-medium text-[#0A1F44] text-sm">{seq.company_name}</span>
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${STATUS_STYLE[seq.status] || ''}`}>{seq.status}</span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">Touch {seq.current_touch}/7 · {seq.primary_contact_name || 'primary decision-maker'}{seq.suspend_reason ? ` · ${seq.suspend_reason}` : ''}</p>
          </div>
          <div className="flex gap-1.5">
            {seq.status === 'active' && (
              <button onClick={() => act(seq.id, 'pause')} disabled={busyId === seq.id} data-testid={`sequence-pause-${seq.id}`}
                className="p-1.5 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50"><Pause className="h-3.5 w-3.5" /></button>
            )}
            {seq.status === 'paused' && (
              <button onClick={() => act(seq.id, 'resume')} disabled={busyId === seq.id} data-testid={`sequence-resume-${seq.id}`}
                className="p-1.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50"><Play className="h-3.5 w-3.5" /></button>
            )}
            {['active', 'paused'].includes(seq.status) && (
              <button onClick={() => act(seq.id, 'cancel')} disabled={busyId === seq.id} data-testid={`sequence-cancel-${seq.id}`}
                className="p-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"><XCircle className="h-3.5 w-3.5" /></button>
            )}
          </div>
        </div>
      ))}
      {selectedCompanyId && (
        <CRMCompanyDetail companyId={selectedCompanyId} config={config} onClose={() => setSelectedCompanyId(null)} onUpdated={load} />
      )}
    </div>
  );
};

const StatCard = ({ label, value, testid }) => (
  <div className="bg-gray-50 rounded-xl p-3" data-testid={testid}>
    <p className="text-2xl font-bold text-[#0A1F44] flex items-center gap-1.5"><Rocket className="h-4 w-4 text-[#D4AF37]" /> {value ?? 0}</p>
    <p className="text-[11px] text-gray-500 mt-0.5">{label}</p>
  </div>
);

export default CRMSequences;
